export type HierarchyLinkInvalidReason = 'self_link' | 'cycle';

export type HierarchyLinkValidation =
  | { isValid: true }
  | { isValid: false; reason: HierarchyLinkInvalidReason };

export interface HierarchyFlowNodeRef {
  id: string;
  parentId: string | null;
}

/**
 * Valide un lien manager (N+1) → subordonné (N) avant l'appel API : refuse
 * qu'une personne soit son propre manager (self-link), et refuse tout lien
 * qui créerait un cycle (le subordonné proposé est déjà un ascendant du
 * manager proposé). Même esprit que `custom-shop-categories-flow-rules.ts`,
 * mais logique de remontée d'ascendants au lieu d'une limite à 2 niveaux —
 * la hiérarchie autorise une profondeur illimitée.
 */
export function validateHierarchyLink(
  nodes: readonly HierarchyFlowNodeRef[],
  parentId: string,
  childId: string
): HierarchyLinkValidation {
  if (parentId === childId) {
    return { isValid: false, reason: 'self_link' };
  }

  const parentByNode = new Map(nodes.map((node) => [node.id, node.parentId]));

  const seen = new Set<string>();
  let current: string | null = parentByNode.get(parentId) ?? null;

  while (current !== null) {
    if (current === childId) {
      return { isValid: false, reason: 'cycle' };
    }
    if (seen.has(current)) break; // garde-fou anti-boucle infinie sur données malformées
    seen.add(current);
    current = parentByNode.get(current) ?? null;
  }

  return { isValid: true };
}
